import {
  IconButton,
  Modal,
  Paper,
  Stack,
  Typography,
  Box,
  Grid2,
  useTheme,
  Alert,
  Button,
  Skeleton,
  Chip,
} from "@mui/material";

import {
  CalendarMonth,
  Close,
  ContactPageOutlined,
  DescriptionOutlined,
  HomeWorkOutlined,
  Person2Outlined,
  RouterOutlined,
  SettingsInputComposite,
} from "@mui/icons-material";
import useStyles from "./styles";
import api from "../../../services/api";
import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import dayjs from "dayjs";

export default function ModalERPv2({
  open = false,
  onClose = () => {},
  ipCliente = "",
  ipPublico = "",
  horario = "",
}) {
  const theme = useTheme();
  const styles = useStyles(theme);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [controller, setController] = useState(null);
  const [apiError, setApiError] = useState({
    status: false,
    message: "",
  });

  const { erpStatus = false, CustomNoRowsOverlay } = useOutletContext();

  useEffect(() => {
    const controller = new AbortController();
    setController(controller);
    if (open) {
      setApiError(false);
      getErpData(controller);
    } else {
      updateController();
      setData({});
    }
    return () => {
      controller.abort();
    };
  }, [open]);

  async function getErpData(controller) {
    setLoading(true);
    try {
      const response = await api.post(
        "/easyLog/getERPinfo",
        { ip: ipCliente, ipPublico, horario },
        { signal: controller.signal }
      );
      setData(response.data);

      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
      setApiError({
        status: true,
        message: error.response?.data?.error || "",
      });

      console.error("Error fetching data:", error);
    }
  }

  const updateController = () => {
    if (controller) {
      controller.abort();
    }
    const newController = new AbortController();
    setController(newController);
  };

  function ModalERPSkeleton() {
    return (
      <Stack direction="column" alignItems="flex-start" width="100%" gap="12px">
        {[...Array(4)].map((_, row) => (
          <Stack key={row} sx={styles.lineStack}>
            <Stack sx={styles.cardStack}>
              <Skeleton variant="text" width={120} />
              <Skeleton variant="text" width="80%" />
            </Stack>
            <Stack sx={styles.cardStack}>
              <Skeleton variant="text" width={120} />
              <Skeleton variant="text" width="80%" />
            </Stack>
          </Stack>
        ))}

        {/* Porta do BNG */}
        <Stack direction="column" width="100%">
          <Skeleton variant="text" width={140} />
          <Skeleton variant="text" width="100%" />
        </Stack>
      </Stack>
    );
  }

  function ModalError() {
    return (
      <Stack sx={styles.stackErro}>
        <Alert severity="error" color="error">
          {apiError.message
            ? apiError.message
            : "Não foi possível buscar os dados do ERP"}
          . Verifique as{" "}
          <Link
            style={{ color: "inherit", fontWeight: 500 }}
            to={"/configuracoes/easylogs"}
          >
            configurações
          </Link>{" "}
          do sistema ou entre em contato com a equipe de suporte.
        </Alert>
        <Stack sx={styles.stackErroIcon}>
          <CustomNoRowsOverlay />
        </Stack>
        <Button
          sx={{
            color: theme.palette.easyLog.primary,
            borderColor: theme.palette.easyLog.primary,
          }}
          variant="outlined"
          onClick={onClose}
          fullWidth
        >
          Fechar
        </Button>
      </Stack>
    );
  }
  return (
    <Modal
      key={ipCliente}
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Paper style={styles.paperModal}>
        {!apiError.status ? (
          <Stack
            direction={"column"}
            alignItems={"start"}
            width={"100%"}
            gap={1}
          >
            <Stack
              width={"100%"}
              justifyContent={"space-between"}
              alignItems={"center"}
              direction={"row"}
            >
              <Stack
                direction={{ sx: "column", md: "row" }}
                gap={1}
                alignItems={"center"}
              >
                <Typography
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 600,
                  }}
                >
                  Detalhes do Cliente
                </Typography>
                <Chip
                  sx={{
                    xs: { marginLeft: "10px" },
                    borderRadius: "7px",
                    padding: "0px",
                    backgroundColor: theme.palette.easyLog.secondary,
                    color: "white",
                  }}
                  label={`IP Privado:${ipCliente}`}
                />
                {/* De qual provedor veio o resultado (só na Rede Neutra). */}
                {data?.provedor && (
                  <Chip
                    size="small"
                    variant="outlined"
                    color={data?.ambiguo ? "warning" : "default"}
                    sx={{ borderRadius: "7px" }}
                    label={data.provedor}
                  />
                )}
              </Stack>
              <IconButton onClick={onClose} sx={{ padding: "6px" }}>
                <Close
                  fontSize="small"
                  //sx={{ color: theme.palette.easyLog.tertiary }}
                />
              </IconButton>
            </Stack>
            {loading ? (
              <ModalERPSkeleton />
            ) : (
              <Stack
                gap={2}
                sx={{ overflowY: "auto", maxHeight: "400px", mt: 1 }}
              >
                {/* Dois provedores com sessão no mesmo IP e instante: o
                    backend devolve os dois em vez de escolher um. */}
                {data?.ambiguo && (
                  <Alert severity="warning">
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                      Mais de um provedor tem sessão neste IP neste horário
                    </Typography>
                    <Typography sx={{ fontSize: "0.8rem", mb: 0.5 }}>
                      Abaixo estão os dados da sessão mais recente. Confira
                      antes de usar este resultado:
                    </Typography>
                    <Stack component="ul" sx={{ m: 0, pl: 2 }}>
                      {(data.candidatos || []).map((candidato) => (
                        <Typography
                          component="li"
                          key={candidato.chave}
                          sx={{ fontSize: "0.8rem" }}
                        >
                          {candidato.provedor} —{" "}
                          {candidato.nomeCliente || "sem nome"} (
                          {candidato.cpfCnpj || "sem CPF/CNPJ"})
                        </Typography>
                      ))}
                    </Stack>
                  </Alert>
                )}

                {/* Sem este aviso, o resultado de outro provedor pareceria
                    a resposta completa. */}
                {data?.provedoresComFalha?.length > 0 && (
                  <Alert severity="info">
                    Busca parcial: sem resposta de{" "}
                    {data.provedoresComFalha.join(", ")}.
                  </Alert>
                )}


                <Box sx={styles.boxBorder}>
                  <Grid2 container spacing={2}>
                    <Grid2 item size={12} sx={{ ...styles.cardStack }}>
                      <Typography sx={styles.cardUserSubTitle}>
                        <Person2Outlined sx={styles.cardUserSubTitleIcon} />{" "}
                        Nome do Cliente
                      </Typography>
                      <Typography sx={styles.cardText}>
                        {data?.nomeCliente || "Não encontrado"}
                      </Typography>
                    </Grid2>
                    <Grid2
                      item
                      size={{ xs: 12, md: 4 }}
                      sx={{ ...styles.cardStack }}
                    >
                      <Typography sx={styles.cardUserSubTitle}>
                        <HomeWorkOutlined sx={styles.cardUserSubTitleIcon} />
                        CPF/CNPJ
                      </Typography>
                      <Typography sx={styles.cardText}>
                        {data?.cpfCnpj || "Não encontrado"}
                      </Typography>
                    </Grid2>

                    <Grid2
                      item
                      size={{ xs: 12, md: 4 }}
                      sx={{ ...styles.cardStack }}
                    >
                      <Typography sx={styles.cardUserSubTitle}>
                        <DescriptionOutlined sx={styles.cardUserSubTitleIcon} />{" "}
                        ID do contrato
                      </Typography>
                      <Typography sx={styles.cardText}>
                        {data?.idContrato || "Não encontrado"}
                      </Typography>
                    </Grid2>
                    <Grid2
                      item
                      size={{ xs: 12, md: 4 }}
                      sx={{ ...styles.cardStack }}
                    >
                      <Typography sx={styles.cardUserSubTitle}>
                        <ContactPageOutlined sx={styles.cardUserSubTitleIcon} />{" "}
                        ID do cliente
                      </Typography>
                      <Typography sx={styles.cardText}>
                        {data?.idCliente || "Não encontrado"}
                      </Typography>
                    </Grid2>
                  </Grid2>
                </Box>
                <Box sx={styles.boxBorder}>
                  <Grid2 container spacing={2}>
                    <Grid2
                      item
                      size={{ xs: 12, md: 6 }}
                      sx={{ ...styles.cardStack }}
                    >
                      <Typography sx={styles.cardUserSubTitle}>
                        <CalendarMonth sx={styles.cardUserSubTitleIcon} />{" "}
                        Inicio da sessão
                      </Typography>
                      <Typography sx={styles.cardText}>
                        {data?.inicioSessao
                          ? dayjs(data?.inicioSessao).format(
                              "DD/MM/YYYY HH:mm:ss"
                            )
                          : "Não registrada"}
                      </Typography>
                    </Grid2>
                    <Grid2
                      item
                      size={{ xs: 12, md: 6 }}
                      sx={{ ...styles.cardStack }}
                    >
                      <Typography sx={styles.cardUserSubTitle}>
                        <CalendarMonth sx={styles.cardUserSubTitleIcon} /> Fim
                        da sessão
                      </Typography>
                      <Typography sx={styles.cardText}>
                        {data?.fimSessao
                          ? dayjs(data?.fimSessao).format("DD/MM/YYYY HH:mm:ss")
                          : "Não registrada"}
                      </Typography>
                    </Grid2>
                  </Grid2>
                </Box>
                <Box sx={styles.boxBorder}>
                  <Grid2 container spacing={2}>
                    <Grid2
                      item
                      size={{ xs: 12, md: 6 }}
                      sx={{ ...styles.cardStack }}
                    >
                      <Typography sx={styles.cardUserSubTitle}>
                        <RouterOutlined sx={styles.cardUserSubTitleIcon} /> Nome
                        do BNG
                      </Typography>
                      <Typography sx={styles.cardText}>
                        {data?.nomeBng || "Não encontrado"}
                      </Typography>
                    </Grid2>
                    <Grid2
                      item
                      size={{ xs: 12, md: 6 }}
                      sx={{ ...styles.cardStack }}
                    >
                      <Typography sx={styles.cardUserSubTitle}>
                        <Box position={"relative"}>
                          <RouterOutlined
                            position={"absolute"}
                            sx={styles.cardUserSubTitleIcon}
                          />
                        </Box>
                        IP do BNG:
                      </Typography>
                      <Typography sx={styles.cardText}>
                        {" "}
                        {data?.ipBng || "Não encontrado"}
                      </Typography>
                    </Grid2>
                    <Grid2 item size={12} sx={{ ...styles.cardStack }}>
                      <Typography sx={styles.cardUserSubTitle}>
                        <SettingsInputComposite
                          sx={styles.cardUserSubTitleIcon}
                        />{" "}
                        Porta do BNG
                      </Typography>
                      <Typography sx={styles.portaBNGTexto}>
                        {data?.portaBng || "Não encontrado"}
                      </Typography>
                    </Grid2>
                  </Grid2>
                </Box>
              </Stack>
            )}
          </Stack>
        ) : (
          <ModalError />
        )}
      </Paper>
    </Modal>
  );
}
